import IndexClient from '@empathybroker/tsclient-index';
import { EnvironmentEnum as IndexEnvironmentEnum } from '@empathybroker/tsclient-index/dist/enums/environment.enum';
import PlayClient from '@empathybroker/tsclient-play';
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum';
import { AuthConfig as PlayAuthConfig } from '@empathybroker/tsclient-play/dist/models/auth-config';
import { Endpoints as PlayEndpoints } from '@empathybroker/tsclient-play/dist/models/endpoints';
import { BoostsConfig } from '@empathybroker/tsclient-play/dist/models/instance/boosts-config';
import { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance';
import { List } from '@empathybroker/tsclient-play/dist/models/list';
import {
  Endpoints as SearchEndpoints,
  ResultMappings as SearchResultMappings,
  SearchClient,
  ServiceTypeEnum as SearchServiceTypeEnum
} from '@empathybroker/tsclient-search';
import StatsClient from '@empathybroker/tsclient-stats';
import { StatsEnvironments as StatsEnvironmentEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-environments';
import { UsersClient } from '@empathybroker/tsclient-users';
import { BrowserStorageService } from '@empathyco/x-storage-service';
import { DefaultSessionService, SessionService } from '@empathyco/x-utils';

import { Container, interfaces } from 'inversify';

import Vue from 'vue';
import Vuex, { Store } from 'vuex';

import { CrudDataTypeEnum, DomainEnum, EnvironmentEnum, FeatureIdEnum, LocaleEnum, RoleEnum } from '../enums';

import {
  ANALYTICS_MESSAGES,
  APP_MESSAGES,
  AUTH_MESSAGES,
  CRUD_MESSAGES,
  ERRORS_MESSAGES,
  FILTERS_MESSAGES,
  INSIGHTS_MESSAGES,
  TOOLS_MESSAGES,
  YEARLY_SUMMARY_MESSAGES
} from '../i18n';

import { ContextState, Dictionary, FeaturesTree } from '../models';

import { ROUTER_CONSTS } from '../router/router.consts';

import { AuthTokenService, StorageService, TaggingLocalService, TaggingService } from '../services';
import { JsonWebTokenService } from '../services/json-web-token.service';
import { LocalStorageService } from '../services/local-storage.service';

import { CRUD_CONSTS } from '../store/crud/crud.consts';

export const CONTAINER = new Container();

export const TYPES: any = {
  Messages: Symbol.for('Messages'),

  StorageService: Symbol.for('StorageService'),
  AuthTokenService: Symbol.for('AuthTokenService'),

  SessionService: Symbol.for('SessionService'),
  TaggingService: Symbol.for('TaggingService'),

  StatsClient: Symbol.for('StatsClient'),
  PlayClient: Symbol.for('PlayClient'),
  IndexClient: Symbol.for('IndexClient'),
  SearchClient: Symbol.for('SearchClient'),
  UsersClient: Symbol.for('UsersClient'),

  Store: Symbol.for('Store'),

  Instances: Symbol.for('Instances'),

  FeaturesTree: Symbol.for('FeaturesTree')
};

CONTAINER.bind<Dictionary<Dictionary>>(TYPES.Messages).toConstantValue({
  [LocaleEnum.ENGLISH]: {
    ...AUTH_MESSAGES[LocaleEnum.ENGLISH],
    ...FILTERS_MESSAGES[LocaleEnum.ENGLISH],
    ...ANALYTICS_MESSAGES[LocaleEnum.ENGLISH],
    ...INSIGHTS_MESSAGES[LocaleEnum.ENGLISH],
    ...CRUD_MESSAGES[LocaleEnum.ENGLISH],
    ...TOOLS_MESSAGES[LocaleEnum.ENGLISH],
    ...ERRORS_MESSAGES[LocaleEnum.ENGLISH],
    ...APP_MESSAGES[LocaleEnum.ENGLISH],
    ...YEARLY_SUMMARY_MESSAGES[LocaleEnum.ENGLISH]
  },
  [LocaleEnum.SPANISH]: {
    ...AUTH_MESSAGES[LocaleEnum.SPANISH],
    ...FILTERS_MESSAGES[LocaleEnum.SPANISH],
    ...ANALYTICS_MESSAGES[LocaleEnum.SPANISH],
    ...INSIGHTS_MESSAGES[LocaleEnum.SPANISH],
    ...CRUD_MESSAGES[LocaleEnum.SPANISH],
    ...TOOLS_MESSAGES[LocaleEnum.SPANISH],
    ...ERRORS_MESSAGES[LocaleEnum.SPANISH],
    ...APP_MESSAGES[LocaleEnum.SPANISH],
    ...YEARLY_SUMMARY_MESSAGES[LocaleEnum.SPANISH]
  }
});

CONTAINER.bind<StorageService>(TYPES.StorageService).toConstantValue(new LocalStorageService());
CONTAINER.bind<AuthTokenService>(TYPES.AuthTokenService).toConstantValue(new JsonWebTokenService());

CONTAINER.bind<SessionService>(TYPES.SessionService)
  .toConstantValue(new DefaultSessionService(new BrowserStorageService(localStorage, 'ebp'), 0));
CONTAINER.bind<TaggingService>(TYPES.TaggingService).toConstantValue(undefined);

CONTAINER.bind<interfaces.Factory<StatsClient>>(TYPES.StatsClient).toFactory<StatsClient>(() => {
  return (context: ContextState): StatsClient => {
    const environment = {
      [EnvironmentEnum.STAGING]: StatsEnvironmentEnum.STAGING,
      [EnvironmentEnum.LIVE]: StatsEnvironmentEnum.LIVE
    }[context.environment];
    return new StatsClient({
      instanceId: context.instanceId,
      environment: environment
    });
  };
});

CONTAINER.bind<interfaces.Factory<PlayClient>>(TYPES.PlayClient).toFactory<PlayClient>(() => {
  return (context: ContextState): PlayClient => {
    const endpoints: PlayEndpoints = {
      [EnvironmentEnum.STAGING]: {
        baseUrl: 'https://api-staging.empathybroker.com/play/v2',
        dashboardBaseUrl: 'https://api-staging.empathybroker.com/play/v1'
      },
      [EnvironmentEnum.LIVE]: {
        baseUrl: 'https://api.empathybroker.com/play/v2',
        dashboardBaseUrl: 'https://api.empathybroker.com/play/v1'
      }
    }[context.environment];
    const authConfig: PlayAuthConfig = {
      [EnvironmentEnum.STAGING]: {
        clientId: 'playboard',
        clientSecret: '2bxfajW9cFA7PAJk'
      },
      [EnvironmentEnum.LIVE]: {
        clientId: 'playboard',
        clientSecret: '5cetCDHk25KdJGuX'
      }
    }[context.environment];
    return new PlayClient(endpoints, authConfig, context.instanceId);
  };
});

CONTAINER.bind<interfaces.Factory<IndexClient>>(TYPES.IndexClient).toFactory<IndexClient>(() => {
  return (context: ContextState, indexToken: string): IndexClient => {
    const environment = {
      [EnvironmentEnum.STAGING]: IndexEnvironmentEnum.STAGING,
      [EnvironmentEnum.LIVE]: IndexEnvironmentEnum.LIVE
    }[context.environment];
    return new IndexClient({
      indexToken: indexToken,
      environment: environment
    });
  };
});

CONTAINER.bind<interfaces.Factory<SearchClient>>(TYPES.SearchClient).toFactory<SearchClient>(() => {
  return (context: ContextState, instanceConfig: Instance): SearchClient => {
    const endpoints: SearchEndpoints = {
      baseUrl: {
        [EnvironmentEnum.STAGING]: 'https://api-staging.empathybroker.com/search/v1/query',
        [EnvironmentEnum.LIVE]: 'https://api.empathybroker.com/search/v1/query'
      }[context.environment]
    };
    if (!!instanceConfig.tools) {
      const boostsConfig: BoostsConfig = instanceConfig.tools[ActionTypeEnum.BOOST];
      if (!!boostsConfig && !!boostsConfig.searchEndpoint) {
        let boostsSearchEndpoint = boostsConfig.searchEndpoint;
        if (!boostsSearchEndpoint.startsWith('/')) {
          boostsSearchEndpoint = `/${ boostsSearchEndpoint }`;
        }
        endpoints.servicesUrls = {
          [SearchServiceTypeEnum.SEARCH_BOOST]: boostsSearchEndpoint
        };
      }
    }
    let resultMappings: SearchResultMappings;
    if (!!instanceConfig.catalog) {
      const instanceResultMappings = instanceConfig.catalog.resultMappings;
      resultMappings = {
        id: instanceResultMappings.id,
        name: instanceResultMappings.name,
        url: instanceResultMappings.url,
        urlPreffix: instanceResultMappings.urlPrefix,
        imageUrl: instanceResultMappings.imageUrl,
        imageUrlPreffix: instanceResultMappings.imageUrlPrefix
      };
    }
    return new SearchClient(endpoints, context.instanceId, undefined, { result: resultMappings });
  };
});

CONTAINER.bind<UsersClient>(TYPES.UsersClient).toDynamicValue(() => undefined);

Vue.use(Vuex);
CONTAINER.bind<Store<any>>(TYPES.Store).toConstantValue(new Vuex.Store({}));

CONTAINER.bind<Promise<Instance[]>>(TYPES.Instances).toDynamicValue(async () => {
  const store = CONTAINER.get<Store<any>>(TYPES.Store);
  try {
    const response = await store.dispatch(`${ CRUD_CONSTS.moduleName }/${ CRUD_CONSTS.actions.fetchData }`, {
      dataType: CrudDataTypeEnum.INSTANCE,
      requestFilters: { enabled: true },
      pagination: { page: 1, itemsPerPage: 200 }
    });
    return (response.data as List<Instance>).items;
  } catch (error) {
    throw error;
  }
});

CONTAINER.bind<FeaturesTree>(TYPES.FeaturesTree).toConstantValue({
  [DomainEnum.SEARCH]: [
    {
      id: FeatureIdEnum.ANALYTICS,
      roles: [
        RoleEnum.SEARCH_ANALYST,
        RoleEnum.SEARCH_MANAGER,
        RoleEnum.SEARCH_ADMIN,
        RoleEnum.EB_ADMIN
      ],
      children: [
        {
          id: FeatureIdEnum.OVERALL,
          routeName: ROUTER_CONSTS.routes.overall
        }, {
          id: FeatureIdEnum.QUERIES,
          routeName: ROUTER_CONSTS.routes.queries
        }
      ]
    }, {
      id: FeatureIdEnum.INSIGHTS,
      roles: [
        RoleEnum.SEARCH_ANALYST,
        RoleEnum.SEARCH_ADMIN,
        RoleEnum.EB_ADMIN
      ],
      routeName: ROUTER_CONSTS.routes.insights,
      menuConfig: {
        ignoreChildren: true
      },
      children: [
        {
          id: FeatureIdEnum.NOW,
          routeName: ROUTER_CONSTS.routes.now
        }, {
          id: FeatureIdEnum.ORIGINS,
          routeName: ROUTER_CONSTS.routes.origins
        },
        {
          id: FeatureIdEnum.KEYWORDS,
          routeName: ROUTER_CONSTS.routes.keywords
        }
      ]
    }, {
      id: FeatureIdEnum.PLAY,
      roles: [
        RoleEnum.SEARCH_MANAGER,
        RoleEnum.SEARCH_ADMIN,
        RoleEnum.EB_ADMIN
      ],
      children: [
        {
          id: FeatureIdEnum.EQUALIZE_DASHBOARD,
          routeName: ROUTER_CONSTS.routes.equalizeDashboard.list
        }, {
          id: FeatureIdEnum.SYNONYM_DASHBOARD,
          routeName: ROUTER_CONSTS.routes.synonymsDashboard.list
        }, {
          id: FeatureIdEnum.LINK,
          routeName: ROUTER_CONSTS.routes.links.list
        }, {
          id: FeatureIdEnum.BOOST,
          routeName: ROUTER_CONSTS.routes.boosts.list
        }
      ]
    }
  ],
  common: [
    {
      id: FeatureIdEnum.INDEX,
      roles: [
        RoleEnum.SEARCH_MANAGER,
        RoleEnum.SEARCH_ADMIN,
        RoleEnum.EB_ADMIN
      ],
      routeName: ROUTER_CONSTS.routes.index
    }, {
      id: FeatureIdEnum.ADMIN,
      roles: [
        RoleEnum.EB_ADMIN
      ],
      menuConfig: {
        displayOnFooter: true,
        displayChildrenOnModal: true
      },
      children: [
        {
          id: FeatureIdEnum.INSTANCE,
          routeName: ROUTER_CONSTS.routes.instances.list,
          menuConfig: {
            ignoreChildren: true
          },
          children: [
            {
              id: FeatureIdEnum.PERMISSIONS
            }, {
              id: FeatureIdEnum.FILTERS
            }, {
              id: FeatureIdEnum.KPIS
            }, {
              id: FeatureIdEnum.CATALOG
            }, {
              id: FeatureIdEnum.TOOLS
            }
          ]
        }
      ]
    }, {
      id: FeatureIdEnum.ACCOUNT,
      routeName: ROUTER_CONSTS.routes.account,
      menuConfig: {
        displayOnFooter: true
      }
    }
  ]
});
